import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { GrantRule } from '../lib/types';
import { actionLabel, formatDateTime } from '../lib/format';
import { Badge, Card, Empty, Notice, PageHead } from '../components/ui';

interface MyConsent {
  id: string;
  state: string;
  granted_at: string;
  application_id: string;
  application_name: string;
  module_id: string;
  module_key: string;
  module_name: string;
  rules: GrantRule[];
}

interface DelegatedApplication {
  id: string;
  name: string;
  description: string | null;
  modules: Array<{ key: string; name: string }>;
}

export function Consents() {
  const consents = useAsync(() => api.get<MyConsent[]>('/api/me/consents'));
  const applications = useAsync(() => api.get<DelegatedApplication[]>('/api/me/consent-applications'));

  async function revoke(id: string): Promise<void> {
    if (!window.confirm('Revoke the consent? The application will immediately be unable to act in your name.'))
      return;
    await api.delete(`/api/me/consents/${id}`);
    consents.reload();
  }

  const active = (consents.data ?? []).filter((consent) => consent.state === 'active');

  return (
    <>
      <PageHead
        title="My consents"
        description="This is where you see which application may access which data in your name. A revocation takes effect immediately."
      />

      {consents.error ? <Notice tone="danger">{consents.error}</Notice> : null}

      <Card title="Granted" tight>
        {active.length ? (
          <table>
            <thead>
              <tr>
                <th>Application</th>
                <th>API</th>
                <th>May do in your name</th>
                <th>Since</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {active.map((consent) => (
                <tr key={consent.id}>
                  <td>{consent.application_name}</td>
                  <td>{consent.module_name}</td>
                  <td>
                    <div className="row">
                      {consent.rules.length ? (
                        consent.rules.map((rule) => (
                          <Badge key={`${rule.resource}.${rule.action}`}>
                            {rule.resource === '*' ? 'everything' : rule.resource} {actionLabel(rule.action)}
                          </Badge>
                        ))
                      ) : (
                        <span className="muted">nothing</span>
                      )}
                    </div>
                  </td>
                  <td className="nowrap">{formatDateTime(consent.granted_at)}</td>
                  <td>
                    <div className="row">
                      <Link className="button small" to={`/consent?application=${consent.application_id}&module=${consent.module_key}`}>
                        Change
                      </Link>
                      <button className="small danger" onClick={() => void revoke(consent.id)}>
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>You have not granted anything to an application yet.</Empty>
        )}
      </Card>

      <Card title="Available applications" hint="applications that accept a consent" tight>
        {applications.data?.length ? (
          <table>
            <tbody>
              {applications.data.map((application) => (
                <tr key={application.id}>
                  <td>
                    <strong>{application.name}</strong>
                    {application.description ? (
                      <div className="muted tiny">
                        {application.description}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="row">
                      {application.modules.map((module) => (
                        <Link
                          key={module.key}
                          className="button small"
                          to={`/consent?application=${application.id}&module=${module.key}`}
                        >
                          Grant {module.name}
                        </Link>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No application is currently asking for a consent.</Empty>
        )}
      </Card>
    </>
  );
}
